import { ChevronLeft, ChevronRight } from 'lucide-react'

import { Button } from '@/components/ui/button'

//
// テーブル下部のページネーション（v0-ui-mock OrdersTable の共通部品化。2026/08/26 FB）。
// 左に表示範囲（X–Y件 / 全Z件）、右にページ番号付きのページャを出す。
// ページングの実体（クライアント側 slice / バックエンド页）は呼び出し側の責務で、
// このコンポーネントは page / pageSize / totalCount から表示とページ遷移だけを担う
//

function getVisiblePages(current: number, total: number): Array<number | 'ellipsis'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1)
  }
  if (current <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis', total]
  }
  if (current >= total - 3) {
    return [1, 'ellipsis', total - 4, total - 3, total - 2, total - 1, total]
  }
  return [1, 'ellipsis', current - 1, current, current + 1, 'ellipsis', total]
}

// ツールバー帯に置く上部ページャ用の、ページ番号を 5 枠に詰めた版（下部の 7 枠に対して 2 枠少ない）。
// 上部ページャは「下端までスクロールせずにページを送る」ための補助で、下部ページャほど重要度が高くない。
// 一方で帯には選択件数・一括アクションも並ぶため、ページ番号が横幅を食うと帯が折り返しやすくなる
// （2026/08/31 「選択時のツールバーが崩れる」FBの一環で、上部だけ幅を詰めると決めた）。
// 前後ページのボタンは残るので、隣のページへ送る操作は 5 枠でも変わらず1クリックでできる
function getCompactVisiblePages(current: number, total: number): Array<number | 'ellipsis'> {
  if (total <= 5) {
    return Array.from({ length: total }, (_, index) => index + 1)
  }
  if (current <= 3) {
    return [1, 2, 3, 'ellipsis', total]
  }
  if (current >= total - 2) {
    return [1, 'ellipsis', total - 2, total - 1, total]
  }
  return [1, 'ellipsis', current, 'ellipsis', total]
}

// ページャを出す必要があるか。0件、または1ページ目で全件が収まる場合は出さない。
// TablePagination 自身の early return と、呼び出し側（DataTable の下部ラッパー）で同じ判定を共有する。
// 呼び出し側でも判定するのは、null を返すケースで border-t 付きの枡だけが残り、テーブル下端の線が
// 二重に見えるのを防ぐため（2026/10/30 利用料画面の v0 FB）
export function hasPagesToShow({ page, pageSize, totalCount }: { page: number; pageSize: number; totalCount: number }) {
  return totalCount > 0 && (totalCount > pageSize || page > 1)
}

export function TablePagination({
  page,
  pageSize,
  totalCount,
  onChangePage,
  ariaLabel,
  testId,
  compact,
}: {
  page: number
  pageSize: number
  totalCount: number
  onChangePage: (page: number) => void
  ariaLabel: string
  testId?: string
  // ページ番号を 5 枠に詰める（上部ページャ用。理由は getCompactVisiblePages のコメント参照）
  compact?: boolean
}) {
  if (!hasPagesToShow({ page, pageSize, totalCount })) {
    return null
  }
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))
  const safePage = Math.min(page, pageCount)
  const start = (safePage - 1) * pageSize + 1
  const end = Math.min(safePage * pageSize, totalCount)

  return (
    // 表示範囲とページ番号は右端へ寄せる。下部ページャだけ justify-between で「左に件数・右にページャ」に
    // なっていたが、上部ページャ（幅いっぱいではないので常に右で隣接する）と見え方が違って読みにくかった
    // （2026/08/31 FB「右下版も右上に合わせて右に寄せてよい」）
    <nav className="flex flex-wrap items-center justify-end gap-3 px-1" aria-label={ariaLabel} data-testid={testId}>
      <p className="text-sm text-muted-foreground">
        {totalCount === 0 ? '0件' : `${start.toLocaleString()}–${end.toLocaleString()}件`} / 全
        {totalCount.toLocaleString()}件
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon-sm"
          onClick={() => {
            onChangePage(Math.max(1, safePage - 1))
          }}
          disabled={safePage === 1}
          aria-label="前のページ"
        >
          <ChevronLeft className="size-4" />
        </Button>
        {(compact === true ? getCompactVisiblePages : getVisiblePages)(safePage, pageCount).map((item, index) =>
          item === 'ellipsis' ? (
            <span
              key={`ellipsis-${index}`}
              className="flex size-7 items-center justify-center text-sm text-muted-foreground"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <Button
              key={item}
              variant={item === safePage ? 'default' : 'outline'}
              size="icon-sm"
              onClick={() => {
                onChangePage(item)
              }}
              aria-label={`${item}ページ目`}
              aria-current={item === safePage ? 'page' : undefined}
            >
              {item}
            </Button>
          ),
        )}
        <Button
          variant="outline"
          size="icon-sm"
          onClick={() => {
            onChangePage(Math.min(pageCount, safePage + 1))
          }}
          disabled={safePage === pageCount}
          aria-label="次のページ"
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </nav>
  )
}
